import './update-status-modal.scss';

import { Modal, notification } from 'antd';
import { createContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button, Select } from '@/components/Shared/ui';
import type { ISelectOption } from '@/components/Shared/ui/select/select';
import { BankNames, DictTypes, SELECTED_BANK_STATUSES } from '@/constants';
import { isDictHasAll, safeKey } from '@/services/helpers/dict';
import { useAppDispatch, useAppSelector } from '@/store';
import { onSetSelectedBank } from '@/store/financingStep.slice';
import { setCurrent } from '@/store/newOrder.slice';
import {
  changeOfferStatus,
  getResponsesByRequestId
} from '@/store/offerStep.slice';
import type { TBankStatuses, TCanChangeBankStatuses } from '@/types/offers';

import { mapBankOfferFromApi } from '../../helpers/mapBankOfferFromApi';
import useAutoCredRequestId from '../../hooks/useAutoCredRequestId';

const STATUSES = [
  'approved',
  'pre_approved',
  'accepted_alternative',
  'on_rework',
  'mobile_client',
  'waiting_of_financing',
  'declined',
  'cancelled'
] as const satisfies TBankStatuses[];

const PRE_OFFERED_STATUSES = [
  'approved',
  'accepted_alternative',
  'declined',
  'error',
  'cancelled'
] as const satisfies TBankStatuses[];

export const EU_3_STEP_STATUSES = [
  'accepted_alternative',
  'approved',
  'client_accepted',
  'mobile_client',
  'vehicle_registration',
  'waiting_for_docs_confirmation',
  'waiting_of_financing'
] as const satisfies TBankStatuses[];

const EU_HIDDEN_STATUSES = [
  'approved',
  'pre_approved'
] as const satisfies TBankStatuses[];

function checkIn<T extends TBankStatuses>(
  array: T[],
  itemToCheck: TBankStatuses
): itemToCheck is T {
  return array.some((item) => item === itemToCheck);
}

// Маппинг с какого статуса можно перейти на какой
const EU_BANK_STATUS_MAP: Partial<
  Record<
    (typeof EU_3_STEP_STATUSES)[number],
    (typeof EU_3_STEP_STATUSES)[number][] | undefined
  >
> = {
  accepted_alternative: ['mobile_client'],
  approved: ['mobile_client'],
  client_accepted: ['mobile_client'],

  mobile_client: ['waiting_for_docs_confirmation'],
  vehicle_registration: ['waiting_for_docs_confirmation'],

  waiting_for_docs_confirmation: ['mobile_client', 'vehicle_registration'],

  waiting_of_financing: ['vehicle_registration']
};

interface StatusFormInterface {
  status: TCanChangeBankStatuses;
}

interface IProps {
  bankId: string;
  isFinancingStep: boolean;
  isOpened: boolean;
  onClose: () => void;
}

type TUpdateStatusContext = {
  onUpdateOfferStatus: (id: string, isFinancingStep?: boolean) => void;
  closeUpdateStatusModal: () => void;
};

export const UpdateStatusContext = createContext<TUpdateStatusContext>({
  onUpdateOfferStatus: () => {},
  closeUpdateStatusModal: () => {}
});

const UpdateStatusModal: React.FC<IProps> = ({
  bankId,
  isFinancingStep,
  isOpened,
  onClose
}) => {
  const dispatch = useAppDispatch();

  const { dicts } = useAppSelector((state) => state.dict);
  const { banksOffers } = useAppSelector((state) => state.offerStep);

  const { requestId } = useAutoCredRequestId();

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit
  } = useForm<StatusFormInterface>({
    mode: 'all',
    delayError: 200
  });

  const [isUpdateStatusLoading, setUpdateStatusLoading] = useState(false);

  const offer = banksOffers.find((bank) => bank.id === bankId);
  const bank = dicts?.[DictTypes.Banks]?.getBy.name[safeKey(offer?.bank.name)];

  const statusesOptions: ISelectOption[] = useMemo(() => {
    if (!offer || !isDictHasAll(dicts, [DictTypes.Statuses, DictTypes.Banks]))
      return [];

    const isEuBankV2 = offer?.bank.name === BankNames.euBankV2;
    const currentStatus = offer.status.code;

    if (
      isFinancingStep &&
      isEuBankV2 &&
      !checkIn(EU_3_STEP_STATUSES, currentStatus)
    )
      return [];

    const euChangableStatuses =
      EU_BANK_STATUS_MAP[currentStatus as keyof typeof EU_BANK_STATUS_MAP];

    return dicts[DictTypes.Statuses].content
      .filter((item) => {
        const code = String(item.code) as TBankStatuses;

        if (isEuBankV2) {
          if (isFinancingStep) {
            if (!euChangableStatuses) return false;
            return checkIn(euChangableStatuses, code);
          } else if (checkIn(EU_HIDDEN_STATUSES, code)) return false;
        }

        return checkIn(STATUSES, code);
      })
      .map((item) => ({ label: item.name, value: String(item.code) }))
      .sort(
        (a, b) =>
          STATUSES.findIndex((status) => status === a.value) -
          STATUSES.findIndex((status) => status === (b.value as TBankStatuses))
      );
  }, [offer, dicts, bankId, isFinancingStep]);

  function handleClose() {
    onClose();
    reset({});
  }

  const handleUpdateStatus = async (data: StatusFormInterface) => {
    if (!offer) return;

    setUpdateStatusLoading(true);
    const isPreOfferedStatus = checkIn(PRE_OFFERED_STATUSES, data.status);

    const response = await dispatch(
      changeOfferStatus({
        bankId: offer.id,
        is_offered: !isPreOfferedStatus,
        offered_uuid: offer.offeredUuid,
        status: data.status
      })
    );

    setUpdateStatusLoading(false);

    if ('error' in response && response.error) {
      notification.error({ title: response.error.message });
    } else {
      const requestResponses = await dispatch(
        getResponsesByRequestId({ reqId: requestId })
      );

      if (getResponsesByRequestId.fulfilled.match(requestResponses)) {
        const financedIndex = requestResponses.payload.findIndex((offer) =>
          SELECTED_BANK_STATUSES.includes(offer.status?.code)
        );

        let currentHandler = 1;
        if (financedIndex !== -1) {
          const financedOffer = mapBankOfferFromApi(
            requestResponses.payload[financedIndex]
          );
          dispatch(onSetSelectedBank(financedOffer));
          currentHandler = 2;
        } else {
          dispatch(onSetSelectedBank(null));
        }
        dispatch(setCurrent(currentHandler));

        notification.success({ title: 'Статус успешно изменен' });
        handleClose();
      } else {
        notification.error({ title: requestResponses.error.message });
        handleClose();
      }
    }
  };

  useEffect(() => {
    if (!offer || !checkIn(STATUSES, offer.status.code)) return;
    reset({ status: offer.status.code as TCanChangeBankStatuses });
  }, [offer?.status.code]);

  return (
    <Modal
      centered
      mask={{ closable: true }}
      className="update-status-modal"
      classNames={{
        header: 'update-status-modal__header'
      }}
      title="Изменить статус предложения"
      open={isOpened}
      onCancel={handleClose}
      footer={null}
    >
      <div className="update-status-modal__bank-info">
        Предложение от <b>{bank?.fullname}</b>
        <p className="update-status-modal__bank-id">(ID: {offer?.extId})</p>
      </div>

      <form onSubmit={handleSubmit(handleUpdateStatus)}>
        <Controller
          name="status"
          control={control}
          rules={{
            required: 'Пожалуйста, выберите статус'
          }}
          render={({ field }) => (
            <div className="update-status-modal__field">
              <Select
                {...field}
                label="Статус"
                selectedValue={statusesOptions.find(
                  (item) => item.value === field.value
                )}
                onChange={(status) => field.onChange(status.value)}
                options={statusesOptions}
                isRequired
              />
            </div>
          )}
        />

        <div className="update-status-modal__actions">
          <Button type="button" onClick={handleClose}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="filled-red"
            disabled={Object.keys(errors).length > 0}
            isLoading={isUpdateStatusLoading}
          >
            Изменить
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UpdateStatusModal;
